import React, { type FC, useEffect, useRef, useState } from 'react'
import './ApplySkeleton.scss'

interface ApplySkeletonProps {
    children?: React.ReactNode
    isLoading?: boolean
    className?: string
    style?: React.CSSProperties
    width?: string | number
    height?: string | number
    borderRadius?: string | number
    placeholder?: React.ReactNode
}

const baseClass = 'ApplySkeleton'

const ApplySkeleton: FC<ApplySkeletonProps> = ({
    children,
    className,
    style,
    isLoading = false,
    width,
    height,
    borderRadius,
    placeholder = 'Loading...',
}) => {
    const parentRef = useRef<HTMLDivElement>(null)
    const [parentFontSize, setParentFontSize] = useState<string>('inherit')

    useEffect(() => {
        if (parentRef.current && window) {
            const computedStyle = window.getComputedStyle(parentRef.current)
            const fontSize = computedStyle.fontSize
            setParentFontSize(fontSize)
        }
    }, [isLoading])

    const dynamicStyles: React.CSSProperties = {
        fontSize: parentFontSize,
        width,
        height,
        borderRadius,
        ...style,
    }

    if (!isLoading) {
        return <>{children}</>
    }

    const childElement = React.isValidElement(children) ? children : null
    const isStringOrNumberChild = typeof children === 'string' || typeof children === 'number'

    if (isStringOrNumberChild || !childElement) {
        return (
            <span ref={parentRef} className={`${baseClass} ${className || ''}`} style={dynamicStyles}>
                <span className={`${baseClass}__placeholder`}>{placeholder}</span>
            </span>
        )
    }

    const childClassName = (childElement?.props as { className?: string })?.className || ''

    const childTagName = typeof childElement.type === 'string' ? childElement.type : 'span'

    const elementProps: React.HTMLAttributes<HTMLElement> & {
        ref?: React.Ref<HTMLDivElement>
    } = {
        className: `${baseClass} ${childClassName} ${className || ''}`,
        style: dynamicStyles,
    }

    if (typeof childElement.type === 'string') {
        elementProps.ref = parentRef
    }

    return React.createElement(
        childTagName,
        elementProps,
        <span className={`${baseClass}__placeholder`}>{placeholder}</span>
    )
}

export default ApplySkeleton
